import styled, { css } from "styled-components";

/** 레이아웃 */
export const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: #ffffff;
  box-shadow: 0 2px 10px rgba(2, 6, 23, 0.04);
  min-height: 56px;
  font-size: 12px;

  @media (max-width: 880px) {
    .btn-row {
      display: none;
    }
  }
`;

export const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const Stat = styled.span`
  color: #0f172a;
  font-size: 14px;
`;

/** 버튼: variant + 미세 인터랙션 + 아이콘 지원  */
type Variant = "neutral" | "success" | "warning" | "info" | "danger";

export const Button = styled.button<{ $variant?: Variant; $active?: boolean }>`
  /* 기본 톤(연한 배경) */
  --fg: #111827;
  --bg: #ffffff;
  --bd: #e5e7eb;
  --hover-bg: #f9fafb;
  --hover-bd: #d1d5db;
  --ring: #2563eb;

  /* 활성(솔리드) 톤 */
  --solid-bg: #111827;
  --solid-fg: #ffffff;
  --solid-bd: #0f172a;

  ${({ $variant }) => {
    switch ($variant) {
      case "success":
        return `
          --fg: #065f46;
          --bg: #ecfdf5;
          --bd: #a7f3d0;
          --hover-bg: #d1fae5;
          --hover-bd: #6ee7b7;
          --ring: #10b981;

          --solid-bg: #10b981;
          --solid-fg: #ffffff;
          --solid-bd: #059669;
        `;
      case "warning":
        return `
          --fg: #7c2d12;
          --bg: #fff7ed;
          --bd: #fed7aa;
          --hover-bg: #ffedd5;
          --hover-bd: #fdba74;
          --ring: #f59e0b;

          --solid-bg: #f59e0b;
          --solid-fg: #0b1020;
          --solid-bd: #d97706;
        `;
      case "info":
        return `
          --fg: #0c4a6e;
          --bg: #eff6ff;
          --bd: #bfdbfe;
          --hover-bg: #dbeafe;
          --hover-bd: #93c5fd;
          --ring: #3b82f6;

          --solid-bg: #3b82f6;
          --solid-fg: #ffffff;
          --solid-bd: #2563eb;
        `;
      case "danger":
        return `
          --fg: #7f1d1d;
          --bg: #fef2f2;
          --bd: #fecaca;
          --hover-bg: #fee2e2;
          --hover-bd: #fca5a5;
          --ring: #ef4444;

          --solid-bg: #ef4444;
          --solid-fg: #ffffff;
          --solid-bd: #dc2626;
        `;
      default:
        return `
          --fg: #111827;
          --bg: #ffffff;
          --bd: #e5e7eb;
          --hover-bg: #f9fafb;
          --hover-bd: #d1d5db;
          --ring: #2563eb;

          --solid-bg: #111827;
          --solid-fg: #ffffff;
          --solid-bd: #0f172a;
        `;
    }
  }}

  position: relative;
  height: 36px;
  padding: 0 12px 0 10px;
  border: 1px solid var(--bd);
  border-radius: 10px;
  color: var(--fg);
  background: var(--bg);
  cursor: pointer;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  box-shadow: 0 1px 0 rgba(17, 24, 39, 0.03),
    0 0 0 0.5px rgba(255, 255, 255, 0.6) inset;

  transition: transform 0.04s ease, border-color 0.15s ease,
    background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease,
    filter 0.15s ease;

  i {
    display: inline-flex;
    line-height: 0;
  }
  svg {
    display: block;
  }

  &:hover:not(:disabled) {
    background: var(--hover-bg);
    border-color: var(--hover-bd);
    box-shadow: 0 3px 14px rgba(17, 24, 39, 0.06),
      0 0 0 0.5px rgba(255, 255, 255, 0.7) inset;
    filter: saturate(1.04);
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
    color: var(--solid-fg);
    background: var(--solid-bg);
    border-color: var(--solid-bd);
    box-shadow: 0 1px 8px rgba(17, 24, 39, 0.08),
      0 0 0 2px rgba(255, 255, 255, 0.6) inset;
    filter: none;
  }

  &:focus-visible {
    outline: 0;
    box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px var(--ring);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    filter: grayscale(0.3);
  }

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.65) 0%,
      rgba(255, 255, 255, 0.22) 35%,
      transparent 60%
    );
    pointer-events: none;
    opacity: 0.7;
    transition: opacity 0.2s ease;
  }
  &:hover::before {
    opacity: 0.9;
  }

  ${({ $active }) =>
    $active &&
    css`
      color: var(--solid-fg);
      background: var(--solid-bg);
      border-color: var(--solid-bd);
      box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.6) inset,
        0 0 0 2px var(--solid-bg);
      &::before {
        opacity: 0.4;
      }
    `}
`;

/** 모바일 메뉴 */
export const MenuWrap = styled.div`
  position: relative;
  margin-left: 8px;

  /* 데스크탑에서는 메뉴 버튼 숨김 */
  display: none;
  @media (max-width: 880px) {
    display: block;
  }
`;

export const MenuButton = styled(Button)`
  height: 36px;
  padding: 0 12px;
  i {
    margin-right: 6px;
  }
`;

export const MenuList = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 180px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  box-shadow: 0 12px 30px rgba(2, 6, 23, 0.18), 0 2px 6px rgba(2, 6, 23, 0.08);
  padding: 8px;
  z-index: 40;
`;

export const MenuItem = styled.button<{ $disabled?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;

  border: 0;
  background: transparent;
  color: ${({ $disabled }) => ($disabled ? "#9ca3af" : "#0f172a")};
  font-size: 14px;
  padding: 10px 10px;
  border-radius: 10px;
  text-align: left;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};

  i {
    line-height: 0;
    display: inline-flex;
  }

  &:hover {
    background: ${({ $disabled }) => ($disabled ? "transparent" : "#f3f4f6")};
  }
`;

/** 메뉴 열릴 때 배경 딤(모바일 UX 향상) */
export const Dim = styled.div`
  @media (max-width: 880px) {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.1);
    z-index: 30;
  }
`;
